/* AO - the anonymous garage.
 *
 * Remembers what the customer appears to drive, in localStorage, with NO login
 * and no modal. Everything else (fitment hints, "shopping for your Model Y")
 * can read it from here.
 *
 * WHY NOT AN ACCOUNT. 596 of 224,319 customers have a vehicle tag today -
 * 0.27%. The ask is not failing because the modal is wrong; it is failing
 * because it interrupts at the moment of highest intent and because
 * /pages/my-garage currently gives the customer nothing back. So this captures
 * silently and asks for nothing. Attaching it to an account is a later,
 * separate decision - see the four states below.
 *
 * WHY THE MENU IS THE RIGHT PLACE TO CAPTURE IT. Purchase history alone cannot
 * resolve GENERATION, and generation is where this store's fitment mistakes
 * live. Measured over 1,759 identified customers (fitment-audit step 17):
 * Model Y 2026 "Juniper" and Model 3 2024 "Highland" have ZERO purchase-proven
 * owners, because the year tags are deliberately over-inclusive (round five:
 * "over-inclusive beats invisible"), so no part proves either generation.
 * Meanwhile 47.2% of customers sit in a narrowed set that ONE menu click
 * collapses - Rivian R1S/R1T is 17.7% on its own. The click is not a nice
 * extra; it is the only available source for the part we cannot infer.
 *
 * THE FOUR STATES, and why a click never simply overwrites:
 *
 *   confirmed     purchase history proves the generation. Strongest.
 *   corroborated  purchase narrows to a set AND a click falls inside it.
 *   claimed       clicks only. Good enough to merchandise on, NOT good enough
 *                 to assert fitment with - browsing is not owning.
 *   conflicting   a click falls outside what purchases prove. Kept, not
 *                 resolved: 1.1% of customers genuinely own two cars, and that
 *                 is a fact worth having rather than an error to squash.
 *
 * This file only ever sets `claimed` - it is the browser half. The server half
 * (fitment-audit/18-backfill-garage.mjs) supplies the purchase evidence, and
 * whatever reconciles them later can read both.
 */
(function () {
  // Loaded from BOTH the header section (so the mobile drawer always has it)
  // and the model-menu section (desktop). Running twice would bind the
  // delegated click listener twice and count every vehicle click double, which
  // would quietly corrupt the repetition weighting that decides which car the
  // customer actually drives.
  if (window.AOGarage) return;

  var KEY = 'ao_garage';
  var VERSION = 1;

  // A single click is weak - people browse, and they shop for other people's
  // cars. Repetition is what separates "I drive this" from "I looked once", so
  // clicks are counted per vehicle and the winner is the most-clicked, with the
  // most recent breaking ties.
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return null;
      var rec = JSON.parse(raw);
      if (!rec || rec.v !== VERSION) return null;
      return rec;
    } catch (e) {
      // Private browsing, disabled storage, or corrupt JSON. The garage is an
      // enhancement - never let it break the page.
      return null;
    }
  }

  function write(rec) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(rec));
    } catch (e) {
      /* quota or private mode - nothing to do */
    }
    return rec;
  }

  function blank() {
    // `genFor` is the model handle the generation belongs to - see record().
    return { v: VERSION, model: null, modelHandle: null, gen: null, genHandle: null, genFor: null, year: null, state: null, clicks: {}, updatedAt: null };
  }

  function winner(clicks) {
    var best = null;
    var bestN = 0;
    for (var handle in clicks) {
      if (!Object.prototype.hasOwnProperty.call(clicks, handle)) continue;
      var entry = clicks[handle];
      if (entry.n > bestN || (entry.n === bestN && best && entry.at > clicks[best].at)) {
        best = handle;
        bestN = entry.n;
      }
    }
    return best;
  }

  /* Reflected onto <html> so CSS and other components can key off the vehicle
     without importing anything or knowing the storage shape. */
  function reflect(rec) {
    var root = document.documentElement;
    if (rec && rec.modelHandle) {
      root.setAttribute('data-ao-vehicle', rec.modelHandle);
      root.setAttribute('data-ao-vehicle-label', rec.model || '');
      root.setAttribute('data-ao-garage-state', rec.state || 'claimed');
    } else {
      root.removeAttribute('data-ao-vehicle');
      root.removeAttribute('data-ao-vehicle-label');
      root.removeAttribute('data-ao-garage-state');
    }
    if (rec && rec.genHandle) root.setAttribute('data-ao-generation', rec.genHandle);
    else root.removeAttribute('data-ao-generation');
    if (rec && rec.year) root.setAttribute('data-ao-vehicle-year', rec.year);
    else root.removeAttribute('data-ao-vehicle-year');
  }

  var AOGarage = {
    get: function () {
      return read();
    },

    /**
     * Record a vehicle the customer showed real intent towards.
     *
     * Called on CLICK only, never on hover: the mega menu's rail switches panes
     * on hover, and a pointer crossing four vehicles on its way to the fifth is
     * not evidence of anything.
     *
     * @param {string} label        display name, e.g. "MODEL Y"
     * @param {string} modelHandle  hub collection handle, e.g. "all-model-y"
     * @param {string} [genHandle]  generation collection handle, when known
     * @param {string} [genLabel]   generation display name, when known
     */
    record: function (label, modelHandle, genHandle, genLabel) {
      if (!modelHandle) return null;
      var rec = read() || blank();
      var now = Date.now();

      if (!rec.clicks[modelHandle]) rec.clicks[modelHandle] = { n: 0, at: 0, label: label };
      rec.clicks[modelHandle].n += 1;
      rec.clicks[modelHandle].at = now;
      rec.clicks[modelHandle].label = label || rec.clicks[modelHandle].label;

      /* A STATED vehicle is not overridden by browsing. The customer picked
         their make, model and year in the finder; clicking around Model 3
         parts afterwards does not mean they sold the car. Clicks keep
         accumulating underneath so the history is intact - they just stop
         deciding what is shown. `Change` on the chip clears it, and a fresh
         finder selection replaces it. */
      if (rec.state === 'stated') {
        rec.updatedAt = new Date(now).toISOString();
        write(rec);
        return rec;
      }

      var top = winner(rec.clicks);
      rec.modelHandle = top;
      rec.model = rec.clicks[top] ? rec.clicks[top].label : label;

      /* The generation only ever describes the vehicle it was clicked under.
       *
       * 🔴 THE PREVIOUS VERSION HAD A HOLE, and Pete found it: pick "Model 3
       * Highland", then click Cybertruck, and the chip read
       * "Cybertruck · Model 3 Highland".
       *
       * It branched on `genHandle && top === modelHandle` / `top !==
       * modelHandle`. Clicking Cybertruck satisfies NEITHER: Cybertruck has no
       * generation chips so `genHandle` is null, and it wins the click count so
       * `top === modelHandle`. Both branches skipped, and the stale Model 3
       * generation survived onto a Cybertruck garage.
       *
       * Tracking WHICH vehicle the generation belongs to removes the case
       * analysis. A generation is kept only while its own vehicle is the one
       * being displayed, so there is no combination of clicks that can pair a
       * generation with the wrong model.
       *
       * Records written before this existed have no `genFor`, so an older
       * generation is dropped on the first click and re-learned on the next -
       * which is the safe direction to be wrong in. */
      if (genHandle) {
        rec.genHandle = genHandle;
        rec.gen = genLabel || null;
        rec.genFor = modelHandle;
      }

      if (rec.gen && rec.genFor !== top) {
        rec.genHandle = null;
        rec.gen = null;
        rec.genFor = null;
      }

      rec.state = 'claimed';
      rec.updatedAt = new Date(now).toISOString();

      write(rec);
      reflect(rec);
      document.documentElement.dispatchEvent(
        new CustomEvent('ao:garage:changed', { bubbles: true, detail: { garage: rec } })
      );
      return rec;
    },

    clear: function () {
      try {
        window.localStorage.removeItem(KEY);
      } catch (e) {
        /* nothing to do */
      }
      reflect(null);
      document.documentElement.dispatchEvent(
        new CustomEvent('ao:garage:changed', { bubbles: true, detail: { garage: null } })
      );
      return null;
    },
  };

  /* --- a stated vehicle -------------------------------------------------- */

  /* ROUND EIGHT: the YMM finder is gone, and with it this file's only reader of
     the URL.

     What used to live here: `YMM_PATTERNS`, `handleize`, `parseVehicleTag` and
     `syncFromUrl`. The Govedia finder navigated to
     `/collections/<handle>/<VehicleTag>`, and rather than hooking the app's own
     JS we read the selection back off that path. With the app retired nothing
     produces those URLs any more, so all four were unreachable and are removed.

     `state()` below is NOT dead with them. It is the stronger of the two ways
     this file records a vehicle, and it is what the mega menu's generation
     chips now call - see ao-garage-endpoint.js. The distinction it encodes
     still holds and still matters: browsing is not owning, but naming your
     generation is a declaration. */

  /**
   * Record a vehicle the customer STATED, rather than one they browsed.
   *
   * This outranks click evidence and is not diluted by it: browsing is not
   * owning, but picking your make, model and year in the finder is as close to
   * a declaration as this store gets without a login.
   */
  AOGarage.state = function (label, modelHandle, genHandle, genLabel, year, display) {
    if (!modelHandle) return null;
    var rec = read() || blank();

    rec.modelHandle = modelHandle;
    rec.model = label || modelHandle;
    rec.genHandle = genHandle || null;
    rec.gen = genLabel || null;
    rec.year = year || null;
    rec.display = display || null;
    rec.state = 'stated';
    rec.updatedAt = new Date().toISOString();

    // Clicks are still counted underneath, so the browse history is not lost -
    // it just stops deciding what is displayed.
    if (!rec.clicks[modelHandle]) rec.clicks[modelHandle] = { n: 0, at: Date.now(), label: rec.model };

    write(rec);
    reflect(rec);
    document.documentElement.dispatchEvent(
      new CustomEvent('ao:garage:changed', { bubbles: true, detail: { garage: rec } })
    );
    return rec;
  };

  window.AOGarage = AOGarage;
  reflect(read());

  /* --- capture and display, for every surface at once ------------------- */

  /* Delegated at the document, deliberately. The desktop panel and the mobile
     drawer are different markup in different sections, and the drawer is moved
     to the end of <body> by Focal's `append-body`, so nothing can hold a
     reference to both. One listener keyed off `data-vehicle-handle` means
     adding a third surface later needs no JS at all - just the attribute. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    var scope = link.closest('[data-vehicle-handle]');
    if (!scope) return;

    AOGarage.record(
      scope.getAttribute('data-vehicle-label'),
      scope.getAttribute('data-vehicle-handle'),
      link.getAttribute('data-generation-handle') || null,
      link.getAttribute('data-generation-label') || null
    );
  });

  /* The header finder button reflects the garage: "What do you drive?" until we
     know, the vehicle once we do. It keeps opening the YMM finder either way -
     this only changes what it says, never what it does.

     The label is rewritten rather than assumed, because the YMM app manipulates
     this button (it strips `disabled` on init). If the app ever replaces the
     contents outright, the span is recreated here rather than silently lost. */
  function paintButtons() {
    var buttons = document.querySelectorAll('[data-ao-garage-button]');
    var rec = read();

    Array.prototype.forEach.call(buttons, function (button) {
      var label = button.querySelector('.ao-garage-button__label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'ao-garage-button__label';
        button.appendChild(label);
      }
      var name = rec ? rec.display || rec.model : null;
      if (rec && rec.state === 'stated' && rec.year) name = rec.year + ' ' + name;
      label.textContent = name ? 'Shopping for ' + name : 'What do you drive?';
    });
  }

  /* Any element with `data-ao-garage-chip` becomes a "Shopping for" chip. */
  function paintChips() {
    var slots = document.querySelectorAll('[data-ao-garage-chip]');
    var rec = read();

    Array.prototype.forEach.call(slots, function (slot) {
      if (!rec || !rec.modelHandle) {
        slot.hidden = true;
        slot.innerHTML = '';
        return;
      }
      slot.hidden = false;
      var text = rec.state === 'stated' && rec.display
        ? (rec.year ? rec.year + ' ' + rec.display : rec.display)
        : rec.gen ? rec.model + ' · ' + rec.gen : rec.model;
      var name = slot.querySelector('.ao-garage-chip__name');
      if (name) {
        name.textContent = text;
        return;
      }
      slot.innerHTML =
        '<span class="ao-garage-chip__eyebrow">Shopping for</span>' +
        '<span class="ao-garage-chip__name"></span>' +
        '<button type="button" class="ao-garage-chip__clear">Change</button>';
      slot.querySelector('.ao-garage-chip__name').textContent = text;
      slot.querySelector('.ao-garage-chip__clear').addEventListener('click', function (event) {
        /* 🔴 stopPropagation IS THE FIX, not tidiness. Pete: "If I click
         * Change, it closes the mega menu and I have to go to the shop button
         * again."
         *
         * ao-shop-menu.js closes the panel from a document-level click
         * listener guarded by `if (this.contains(event.target)) return;`.
         * Clearing the garage repaints this chip and REMOVES the button that
         * was just clicked, so by the time that listener runs on the bubble
         * phase the target is detached from the document - `contains()` is
         * false, the panel reads it as an outside click, and closes.
         *
         * Keeping the panel open is also the behaviour that makes sense:
         * "Change" means the customer wants to pick a different vehicle, and
         * the rail to pick it from is right there. */
        event.stopPropagation();
        AOGarage.clear();
      });
    });
  }

  function paintAll() {
    paintChips();
    paintButtons();
  }

  document.documentElement.addEventListener('ao:garage:changed', paintAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }

  // The YMM app initialises this button after us and strips its `disabled`
  // class. Repaint once the page has settled so a late app rewrite cannot leave
  // the label stale or blank.
  window.addEventListener('load', paintAll);
})();
